import { Maybe, rd, maybe } from '@passionware/monads';
import { Hash, Settings } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterConfigBase } from 'v5/patterns/filters/FilterWidget.types';
import { MergeServices } from 'v5/platform/ts/services';
import {
  MetastructureAggregateRequest,
  MetastructureRequest,
  ResolvedMetastructureField,
  resolvedMetastructureFieldUtils,
  WithMetastructureMappingService
} from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithProductionTypeService } from 'v5/services/internal/ProductionTypeService/ProductionTypeService';
import { WithGroupsService } from 'v5/services/io/GroupsService/GroupsService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import { createCoreFieldFilterConfig } from './metastructure-filters/core';
import { createCustomFieldFilterConfig } from './metastructure-filters/custom';
import {
  CORE_FIELD_NAMES,
  CoreFieldName
} from './metastructure-filters/core/fields';
import { timestampFilter } from 'v5/core/standard-date-filter/timestamp-filter';

/** Aggregate request, optionally narrowed to one subtype. */
export type FilterMetastructureRequest = MetastructureAggregateRequest & {
  subtypeId?: number;
};

export function isSpecificMetastructureRequest(
  request: FilterMetastructureRequest
): request is MetastructureRequest {
  return request.subtypeId != null;
}

export function useMetastructureFilters(
  metastructureRequest: Maybe<FilterMetastructureRequest>,
  services: MergeServices<
    [
      WithMetastructureMappingService,
      WithResourceTypeService,
      WithResourceService,
      WithTagService,
      WithGroupsService,
      WithProductionTypeService
    ]
  >
) {
  const { t } = useTranslation('patterns');
  const { t: coreT } = useTranslation('core');

  const resourceTypes = services.resourceTypeService.useResourceTypes(
    maybe.map(metastructureRequest, request => request.scope)
  );

  // Get production types for name resolution (called at top level for hooks rules)
  const productionTypes = services.productionTypeService?.useProductionTypes();
  const resourceTypeNames = rd.map(resourceTypes, types =>
    types.reduce(
      (acc, type) => {
        acc[type.id] = type.name || `Resource Type ${type.id}`;
        return acc;
      },
      {} as Record<number, string>
    )
  );

  const aggregateRequests =
    services.metastructureMappingService.useMetastructureAggregateRequests(
      metastructureRequest
    );

  const requests = maybe.mapOrElse(
    metastructureRequest,
    request =>
      isSpecificMetastructureRequest(request) ? [request] : aggregateRequests,
    []
  );

  const metastructures = services.metastructureMappingService.useMetastructures(
    requests ?? []
  );

  // Generate dynamic field configs for all visible filterable fields (both core and custom)
  return rd.useMemoMapMonadic(
    rd.combine({
      metastructures: rd.combine(
        metastructures.reduce(
          (acc, metastructure, index) => {
            acc[`metastructure_${index}`] = metastructure;
            return acc;
          },
          {} as Record<string, any>
        )
      ),
      resourceTypeNames
    }),
    ({ metastructures: metastructuresMap, resourceTypeNames: names }) => {
      const configs: Record<string, FilterConfigBase> = {};
      const coreFieldChildren: string[] = [];
      const customFieldChildrenByTypeId: Record<string, string[]> = {};

      // add fixed core field: createdAt:
      configs[CORE_FIELD_NAMES.CREATED_AT] = timestampFilter.createFilterConfig(
        coreT('core_fields.createdAt'),
        coreT
      );
      // add to core field children
      coreFieldChildren.push(CORE_FIELD_NAMES.CREATED_AT);

      // Process each metastructure with its corresponding request
      requests.forEach((request, index) => {
        const metastructure = (metastructuresMap as any)[
          `metastructure_${index}`
        ];

        // Create a typeId label for grouping
        let typeIdLabel = '';
        if (request.type === 'resource') {
          typeIdLabel = ` (${names[request.subtypeId] || `Type ${request.subtypeId}`})`;
        } else if (request.type === 'production' && productionTypes) {
          const productionTypeName = rd
            .tryGet(productionTypes)
            ?.find(type => type.id === request.subtypeId)?.name;
          typeIdLabel = ` (${productionTypeName || `Type ${request.subtypeId}`})`;
        } else {
          const typeLabel = t(`EntityTypes.${request.type}`);
          typeIdLabel = ` (${typeLabel} ${request.subtypeId})`;
        }

        // Process fields from this metastructure
        metastructure.forEach((field: ResolvedMetastructureField) => {
          if (!field.active) return;

          if (resolvedMetastructureFieldUtils.isCustomField(field)) {
            // Custom field
            if (field.customFieldDefinition.filterable) {
              const fieldName = `custom_${field.customFieldDefinition.id}`;
              const filterConfig = createCustomFieldFilterConfig(
                field.customFieldDefinition,
                coreT
              );
              if (filterConfig) {
                configs[fieldName] = filterConfig;
                // Group custom fields by typeId
                const groupKey = `custom${typeIdLabel}`;
                if (!customFieldChildrenByTypeId[groupKey]) {
                  customFieldChildrenByTypeId[groupKey] = [];
                }
                if (
                  !customFieldChildrenByTypeId[groupKey].includes(fieldName)
                ) {
                  customFieldChildrenByTypeId[groupKey].push(fieldName);
                }
              }
            }
          } else if (resolvedMetastructureFieldUtils.isCoreField(field)) {
            // Core field - create filter config based on field name
            const filterConfig = createCoreFieldFilterConfig(
              coreT,
              field.name as CoreFieldName,
              request,
              services
            );
            if (filterConfig) {
              // Translate core field labels
              const translatedLabel = t(`CoreFields.${field.name}`, field.name);
              configs[field.name] = {
                ...filterConfig,
                label: translatedLabel
              };
              // TODO: Remove this once the organisation filter is implemented
              if (field.name === CORE_FIELD_NAMES.ORGANISATION_ID) {
                configs[field.name].label += ' (Coming Soon)';
                configs[field.name].disabled = true;
              }
              // Add to core field children for grouping
              if (!coreFieldChildren.includes(field.name)) {
                coreFieldChildren.push(field.name);
              }
            }
          }
        });
      });

      const groups: Array<{
        name: string;
        icon: ReactNode;
        children: string[];
      }> = [];
      if (coreFieldChildren.length > 0) {
        groups.push({
          name: t('FilterGroups.coreFields'),
          icon: <Settings />,
          children: coreFieldChildren
        });
      }

      // Add custom field groups by typeId
      Object.entries(customFieldChildrenByTypeId).forEach(
        ([groupKey, children]) => {
          if (children.length > 0) {
            groups.push({
              name: `${t('FilterGroups.customFields')}${groupKey.replace('custom', '')}`,
              icon: <Hash />,
              children
            });
          }
        }
      );

      const order = groups.flatMap(group => group.children);

      return rd.of({
        configs,
        groups,
        order
      });
    }
  );
}
